import { usePostHog } from 'posthog-js/react'
import { v4 as uuidv4 } from 'uuid'
import { useSalaryFilters } from '../filters/use-salary-filters'

type G2GModuleName = 'histogram' | 'pane' | 'main' | 'modal'
type ModalTrigger = 'salary-search' | 'pane-click' | 'pagination' | 'other'

interface FilterOption {
  label: string
  value: string
}

function isFilterOption(value: unknown): value is FilterOption {
  return (
    typeof value === 'object' &&
    value !== null &&
    'label' in value &&
    'value' in value
  )
}

export function getFilterLabel(filter: unknown): string {
  return isFilterOption(filter) ? filter.label : ''
}

interface G2GTrackingProps {
  professionName: string
  specialtyName: string
  locationName: string
  salaryId?: string
  moduleName: G2GModuleName
  modalTrigger?: ModalTrigger
}

export function useG2GTracking() {
  const posthog = usePostHog()

  const trackG2GView = ({
    professionName,
    specialtyName,
    locationName,
    salaryId,
    moduleName,
    modalTrigger,
  }: G2GTrackingProps) => {
    const pageId = uuidv4()

    const baseProps = {
      screen_name: 'salary_results',
      profession_name: professionName,
      specialty_name: specialtyName,
      location_name: locationName,
      page_id: pageId,
      module_name: moduleName,
      ...(salaryId && { salary_id: salaryId }),
      ...(modalTrigger && { trigger: modalTrigger }),
    }

    posthog.capture('viewed_g2g_prompt', baseProps)
  }

  const trackSignupClick = (moduleName: G2GModuleName) => {
    posthog.capture('clicked_signup_all', {
      screen_name: 'salary',
      module_name: `g2g-${moduleName}`,
    })
  }

  const useG2GViewTracking = ({
    moduleName,
    salaryId,
    isAuthenticated = false,
  }: {
    moduleName: G2GModuleName
    salaryId?: string
    isAuthenticated?: boolean
  }) => {
    const { salaryFilterControls } = useSalaryFilters({
      isAuthenticated,
    })

    return () =>
      trackG2GView({
        professionName: getFilterLabel(salaryFilterControls?.profession),
        specialtyName: getFilterLabel(salaryFilterControls?.specialty),
        locationName: getFilterLabel(salaryFilterControls?.state?.label),
        moduleName,
        modalTrigger: 'salary-search',
        salaryId,
      })
  }

  return { trackG2GView, trackSignupClick, useG2GViewTracking }
}
